<template>
    <div id="setgoal-page">
        <form class="set-goal" @submit.prevent="setGoal">
            <h3>Set Your Savings Goal</h3>
            <div class="field">
                <label for="downpayment">Down Payment Goal</label>
                <currency-input v-model="downpaymentGoal" />
            </div>
            <div class="slide-field time-frame">
                <label>Time Frame</label>
                <output>{{ timeFrame|monthSuffix }}</output>
                <slider v-model="timeFrameIndex" steps="47" />
            </div>
            <div class="monthly-goal">
                <p>Monthly amount to reach your goal: </p>
                <output>{{ perMonth|currency }}</output>
            </div>
            <div class="buttons">
                <button>Set This Goal</button>
            </div>
        </form>
        <div class="payment-panel">
            <h3>Mortgage Calculator</h3>
            <div class="slide-field">
                <label>Home Price</label>
                <output>{{ homePrice|currency }}</output>
                <slider v-model="homePriceIndex" steps="16" />
            </div>
            <div class="slide-field">
                <label>Interest Rate</label>
                <output>{{ interestRate }}%</output>
                <slider v-model="interestRateIndex" steps="24" />
            </div>
            <div class="slide-field">
                <label>Down Payment</label>
                <output>{{ downPayment|currency }} ({{ downPaymentPercent }}%)</output>
                <slider v-model="downPaymentIndex" steps="96" />
            </div>
            <h3>Monthly Payment</h3>
            <div class="payment-chart">
                <div class="principal">
                    <p>Principal &amp; Interest</p>
                    <output>{{ principalPayment|currency }}</output>
                </div>
                <div class="chart">
                    <svg ref="chart">
                        <defs>
                            <filter id="shadow">
                                <feDropShadow dx="0" dy="0" stdDeviation="3" flood-color="black" flood-opacity="0.5"/>
                            </filter>
                        </defs>

                        <g class="label">
                            <circle cx="0" cy="0" fill="#fff" r="60.2" />
                            <text>
                                <tspan class="amount" dx="-23%" dy="-0.4em">{{ monthlyPayment|currency }}*</tspan>
                            </text>
                            <text>
                                <tspan class="small" dx="-25" dy="1em">Monthly</tspan>
                                <tspan class="small" dx="-53" dy="1.1em">Payment</tspan>
                            </text>
                        </g>
                        
                    </svg>
                </div>
                <div class="homeowners-ins">
                    <p>Homeowners Insurance</p>
                    <output>{{ homeownersInsPayment|currency }}</output>
                </div>
                <div class="mortgage-ins">
                    <p>Mortgage Insurance</p>
                    <output>{{ mortgageInsPayment|currency }}</output>
                </div>
                <div class="property-taxes">
                    <p>Property Taxes</p>
                    <output>{{ propertyTaxPayment|currency }}</output>
                </div>
                <div class="disclaimer">
                    <p>*Estimates based on national average</p>
                    <div class="state">
                        <label>Select a state for a more accurate estimate</label>
                        <select v-model="state">
                            <option value>--</option>
                            <option>Alabama</option>
                            <option>Alaska</option>
                            <option>Arizona</option>
                            <option>Arkansas</option>
                            <option>California</option>
                            <option>Colorado</option>
                            <option>Connecticut</option>
                            <option>Delaware</option>
                            <option>District of Columbia</option>
                            <option>Florida</option>
                            <option>Georgia</option>
                            <option>Hawaii</option>
                            <option>Idaho</option>
                            <option>Illinois</option>
                            <option>Indiana</option>
                            <option>Iowa</option>
                            <option>Kansas</option>
                            <option>Kentucky</option>
                            <option>Louisiana</option>
                            <option>Maine</option>
                            <option>Maryland</option>
                            <option>Massachusetts</option>
                            <option>Michigan</option>
                            <option>Minnesota</option>
                            <option>Mississippi</option>
                            <option>Missouri</option>
                            <option>Montana</option>
                            <option>Nebraska</option>
                            <option>Nevada</option>
                            <option>New Hampshire</option>
                            <option>New Jersey</option>
                            <option>New Mexico</option>
                            <option>New York</option>
                            <option>North Carolina</option>
                            <option>North Dakota</option>
                            <option>Ohio</option>
                            <option>Oklahoma</option>
                            <option>Oregon</option>
                            <option>Pennsylvania</option>
                            <option>Rhode Island</option>
                            <option>South Carolina</option>
                            <option>South Dakota</option>
                            <option>Tennessee</option>
                            <option>Texas</option>
                            <option>Utah</option>
                            <option>Vermont</option>
                            <option>Virginia</option>
                            <option>Washington</option>
                            <option>West Virginia</option>
                            <option>Wisconsin</option>
                            <option>Wyoming</option>
                        </select>				
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import {mapState} from 'vuex'
import Slider from '../components/Slider.vue'
import CurrencyInput from '../components/CurrencyInput.vue'

import * as d3 from 'd3'

import {addMonths} from 'date-fns'

import {states, propertyTaxes} from '../fakeData'

export default {
    name: 'setgoal-page',
    components: {Slider, CurrencyInput},
    data() {
        return {
            homePriceIndex: 3,
            interestRateIndex: 7,
            downPaymentIndex: 10,
            downpaymentGoal: 0,
            timeFrameIndex: 8,
        }
    },
    watch: {
        homePriceIndex() {
            this.updateChart()
        },
        interestRateIndex() {
            this.updateChart()
        },
        downPaymentIndex() {
            this.updateChart()
        },
    },
    filters: {
        monthSuffix(input) {
            let suffix = input == 1 ? 'Month' : 'Months'
            return `${input} ${suffix}`
        }
    },
    computed: {
        ...mapState(['goalInfo']),
        state: {
            get() {
                return this.$store.state.state
            },
            set(v) {
                this.$store.dispatch('setState', v)
            }
        },
        homePrice() {
            let v = 50000 * (this.homePriceIndex + 2);
            return Number(v.toPrecision(3))
        },
        interestRate() {
            let v = 0.25 * (this.interestRateIndex + 8);
            return Number(v.toFixed(2))
        },
        downPayment() {
            // return (this.downPayment / this.homePrice * 100).toFixed(2)
            return (this.downPaymentPercent / 100 * this.homePrice).toFixed(2)
        },
        downPaymentPercent() {
            let v = 0.25 * (this.downPaymentIndex + 4);
            return Number(v.toFixed(2))
        },
        principalPayment() {
            let p = this.homePrice - this.downPayment;
            let r = this.interestRate / 1200
            let n = 360
            return p * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1)
        },
        timeFrame() {
            return this.timeFrameIndex + 1
        },
        homeownersInsPayment() {
            return this.homePrice * 0.0057 / 12
        },
        mortgageInsPayment() {
            if (this.downPaymentPercent >= 20) {
                return 0
            }

            return (this.homePrice - this.downPayment) * 0.006 / 12
        },
        propertyTaxPayment() {
            let tax;
            if (this.state) {
                tax = propertyTaxes[this.state] / 100
            }
            else {
                let i = parseInt(Math.floor(Object.values(propertyTaxes).length / 2))
                tax = Object.values(propertyTaxes)[i] / 100
            }
            return this.homePrice * tax / 12
        },
        monthlyPayment() {
            return (this.principalPayment + this.homeownersInsPayment + this.mortgageInsPayment + this.propertyTaxPayment);
        },
        donutData() {
            return [
                {name: 'Principal', value: (this.principalPayment / this.monthlyPayment).toFixed(2)},
                {name: 'Homeowners Insurance', value: (this.homeownersInsPayment / this.monthlyPayment).toFixed(2)},
                {name: 'Mortgage Insurance', value: (this.mortgageInsPayment / this.monthlyPayment).toFixed(2)},
                {name: 'Property Tax', value: (this.propertyTaxPayment / this.monthlyPayment).toFixed(2)},
            ]
        },
        perMonth() {
            return this.downpaymentGoal / this.timeFrame
        }
    },
    methods: {
        setGoal() {
            this.$store.dispatch('setGoalInfo', {
                amount: this.downpaymentGoal,
                time: this.timeFrame,
                date: addMonths(new Date(), this.timeFrame)
            })
            
        },
        updateChart() {
            let pie = d3.pie()
                .sort(null)
                .value(d => d.value)

            let color = d3.scaleOrdinal()
                .domain(this.donutData.map(d => d.name))
                .range(['#8CCAE1', '#D07AB3', '#9AEB6B', '#F3A02A'])


            // let size = Math.min(this.$refs.chart.clientWidth, this.$refs.chart.clientHeight)
            let size = this.$refs.chart.clientWidth
            let radius = size / 2 
            let arc = d3.arc()
                .innerRadius(0)
                .outerRadius(radius * 0.9)


            d3.select(this.$refs.chart)
                .attr('viewBox', [-radius, -radius, size, size])
                .selectAll('path')
                .data(pie(this.donutData))
                .join('path')
                    .attr('fill', d => color(d.data.name))
                    .attr('d', arc)

            let g = d3.select(this.$refs.chart)
                .select('g.label')
                .raise()
                .select('circle')
                .attr('r', radius * 0.7)

        },
    },
    mounted() {
        this.updateChart()
        this.$nextTick(() => {
            if (this.goalInfo && this.goalInfo.amount) {
                this.downpaymentGoal = this.goalInfo.amount;
            }
            if (this.goalInfo && this.goalInfo.time) {
                this.timeFrameIndex = this.goalInfo.time - 1
            }
        })
    },
}
</script>
<style lang="scss">
#setgoal-page {
    flex: 1 1 100%;
    // display: grid;
    // grid-template-rows: minmax(calc(50vh - 77.5px), 1fr) minmax(calc(50vh - 77.5px), 1fr);
    display: flex;
    flex-flow: column nowrap;

    background-image: linear-gradient(to bottom, #25e6bb 0%, #0a8de5 100%);

    .set-goal {
        flex: 1 1 auto;
        display: flex;
        flex-flow: column nowrap;
        justify-content: space-between;

        padding: 12px 0;
        
        .field {
            padding: 0 20px;

            input {
                flex: 0 1 auto;
                font-size: 12px;
                max-width: 25vmin;
            }
        }

        label {
            text-align: left;
        }

        .time-frame {
            .thumb {
                background-repeat: no-repeat;
                background-size: 10px;
                background-position: 50% 50%;
                background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16"><circle cx="8" cy="8" r="8" fill="%232AF598" /></svg>');
            }
        }

        .monthly-goal {
            display: flex;
            flex-flow: row nowrap;
            padding: 0 20px;
            margin-bottom: 16px;

            justify-content: space-between;

            font-size: 13px;
            p {
                flex: 0 0 70%;
                text-align: left;
                font-size: 13px;
                font-weight: bold;
            }
            output {
                font-weight: bold;
            }
        }
        button {
            display: inline-block;
            color: black;
            background-image: linear-gradient(to bottom, #fff 0%, #fff 36%, #E8E8E8 100%);
            border-radius: 12px;

            font-weight: bold;
            text-shadow: 0 0 1px white;

            text-decoration: none;
            font-size: 12px;
            padding: 10px 20px;

            &:active {
                box-shadow: 0px 0px 4px inset rgba(0, 0, 0, 0.25);
            }
        }
    }

    .payment-panel {
        flex: 1 1 auto;
        background: white;
        padding: 12px 0;

        .slide-field {
            .slider {
                background: #c7c7c7;
                height: 2px;
            }
            .track-fill {
                height: 2px;
            }
            .thumb {
                width: 16px;
                height: 16px;
                top: -7px;
                background-repeat: no-repeat;
                background-size: 6px;
                background-position: 50% 50%;
                background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16"><circle cx="8" cy="8" r="8" fill="white" /></svg>');
            }
        }
    }

    h3 {
        font-size: 18px;
        font-weight: bold;
        margin-bottom: 14px;
    }

    .slide-field {
        margin-bottom: 16px;
        display: grid;
        grid-template-areas: "label value"
                             "slide slide";

        padding: 0 20px;
        label {
            text-align: left;
            grid-area: label;
        }
        output {
            text-align: right;
            grid-area: value;
        }

        .slider {
            margin: 10px 0;
            grid-area: slide;
        }
    }


    .payment-chart {
        display: grid;
        padding: 0 8px;
        grid-template-areas: "pi ch ch hi"
                             "mi ch ch pt"
                             "ds ds ds ds";

        // grid-template-columns: 25vmin 0.5fr 0.5fr 25vmin;
        grid-template-columns: 25% 0.5fr 0.5fr 25%;
        // height: 100%;

        output {
            font-weight: bold;
        }
        p {
            font-size: 12px;
            font-weight: bold;
        }

        .disclaimer {
            grid-area: ds;

            .state {
                margin: 12px 0 8px;
                display: grid;
                grid-template-columns: auto 1fr;

                label {
                    text-align: left;
                    font-size: 12px;
                    font-weight: bold;
                }
                select {

                    border-radius: 2px;
                    border: solid 1px #a9a9a9;
                    padding: 8px;
                    appearance: none;


                    background-repeat: no-repeat;
                    background-position: 98% 50%;
                    background-size: 14px;
                    background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 20"><path fill="%23a9a9a9" d="M0,0 L32,0 L16,20" stroke-width="0.5" stroke="%23a9a9a9" stroke-linejoin="round" /></svg>');
                }
            }

        }

        .chart {
            grid-area: ch;

            svg {
                width: 100%;
                height: auto;

                circle {
                    filter: url(#shadow);
                }

                tspan.amount {
                    font-weight: bold;
                    font-size: 16px;
                }
                tspan.small {
                    font-weight: bold;
                    font-size: 12px;
                }
            }
        }
        .principal {
            grid-area: pi;

            output {
                color: #8ccae1;
            }
        }
        .homeowners-ins {
            grid-area: hi;

            output {
                color: #d07ab3;
            }
        }

        .mortgage-ins {
            grid-area: mi;

            output {
                color: #9aeb6b;
            }
        }

        .property-taxes {
            grid-area: pt;

            output {
                color: #f3a02a;
            }
        }
    }
}
</style>
